import React, { useState, useRef, useCallback } from 'react';
import { useProgress } from '../context/ProgressContext';
import { Confetti, ToastContainer } from '../components';
import type { ToastData } from '../components';
import { canMakeRequest, recordRequest, getTimeUntilNextRequest, getCachedResponse, cacheResponse } from '../lib/storage';
import type { GenerateTrophyRequest, GenerateTrophyResponse, AIGeneratedTrophy } from '../types';
import { v4 as uuidv4 } from 'uuid';
import './AIGeneratorPage.css';

export const AIGeneratorPage: React.FC = () => {
  const { addAITrophy, aiTrophies } = useProgress();
  
  const [formData, setFormData] = useState<GenerateTrophyRequest>({
    title: '',
    theme: '',
    style: '',
  });
  const [loading, setLoading] = useState(false);
  const [generatedTrophy, setGeneratedTrophy] = useState<{
    name: string;
    description: string;
    imageDataUrl: string;
  } | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showConfetti, setShowConfetti] = useState(false);
  const [toasts, setToasts] = useState<ToastData[]>([]);
  const [rateLimitWait, setRateLimitWait] = useState(0);

  const abortControllerRef = useRef<AbortController | null>(null);

  const addToast = useCallback((message: string, type: ToastData['type']) => {
    const id = Date.now().toString();
    setToasts((prev) => [...prev, { id, message, type }]);
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Titlul este obligatoriu';
    }
    if (!formData.theme.trim()) {
      newErrors.theme = 'Tema este obligatorie';
    }
    if (!formData.style.trim()) {
      newErrors.style = 'Stilul este obligatoriu';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e: React.FormEvent, ignoreCache = false) => {
    e.preventDefault();

    if (!validateForm()) return;

    // Check rate limit
    if (!canMakeRequest()) {
      const waitTime = Math.ceil(getTimeUntilNextRequest() / 1000);
      setRateLimitWait(waitTime);
      addToast(`Prea multe cereri. Așteaptă ${waitTime} secunde.`, 'error');
      return;
    }

    const prompt = `${formData.title} ${formData.theme} ${formData.style}`;

    // Check cache
    if (!ignoreCache) {
      const cached = await getCachedResponse(prompt);
      if (cached) {
        setGeneratedTrophy({
          name: cached.trophyName,
          description: cached.trophyDescription,
          imageDataUrl: cached.imageDataUrl,
        });
        addToast('Trofeu încărcat din cache!', 'info');
        return;
      }
    }

    setLoading(true);
    setGeneratedTrophy(null);
    abortControllerRef.current = new AbortController();

    try {
      recordRequest();

      const response = await fetch('/api/generate-trophy', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
        signal: abortControllerRef.current.signal,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Eroare la generare');
      }

      const data: GenerateTrophyResponse = await response.json();

      if (data.error) {
        throw new Error(data.error);
      }

      const trophy = {
        name: data.trophyName,
        description: data.trophyDescription,
        imageDataUrl: data.imageDataUrl || data.imageUrl || '',
      };

      setGeneratedTrophy(trophy);
      await cacheResponse(prompt, {
        trophyName: trophy.name,
        trophyDescription: trophy.description,
        imageDataUrl: trophy.imageDataUrl,
      });
      setShowConfetti(true);
      addToast('Trofeu generat cu succes!', 'success');
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        addToast('Generarea a fost anulată', 'info');
      } else {
        addToast(error instanceof Error ? error.message : 'Eroare necunoscută', 'error');
      }
    } finally {
      setLoading(false);
      abortControllerRef.current = null;
    }
  };

  const handleCancel = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  };

  const handleSaveTrophy = async () => {
    if (!generatedTrophy) return;

    const trophy: AIGeneratedTrophy = {
      id: uuidv4(),
      name: generatedTrophy.name,
      description: generatedTrophy.description,
      imageDataUrl: generatedTrophy.imageDataUrl,
      createdAt: new Date().toISOString(),
      prompt: `${formData.title} - ${formData.theme} - ${formData.style}`,
    };

    await addAITrophy(trophy);
    addToast('Trofeul a fost salvat în colecție!', 'success');
    setGeneratedTrophy(null);
    setFormData({ title: '', theme: '', style: '' });
  };

  const styleOptions = [
    { value: 'cartoon', label: 'Cartoon' },
    { value: 'realistic', label: 'Realist' },
    { value: 'pixel-art', label: 'Pixel Art' },
    { value: 'minimalist', label: 'Minimalist' },
    { value: 'watercolor', label: 'Acuarelă' },
    { value: '3d-render', label: '3D Render' },
  ];

  return (
    <div className="ai-generator-page">
      <Confetti isActive={showConfetti} onComplete={() => setShowConfetti(false)} />
      <ToastContainer toasts={toasts} onRemove={removeToast} />

      <header className="generator-header">
        <h1>🤖 Generator de Trofee AI</h1>
        <p className="generator-subtitle">
          Creează trofee unice cu ajutorul inteligenței artificiale!
        </p>
      </header>

      <div className="generator-content">
        <form className="generator-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="title">Titlu Trofeu</label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              placeholder="ex: Super Coder, Dragon Master"
              disabled={loading}
              className={errors.title ? 'error' : ''}
            />
            {errors.title && <span className="error-message">{errors.title}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="theme">Temă</label>
            <input
              type="text"
              id="theme"
              name="theme"
              value={formData.theme}
              onChange={handleInputChange}
              placeholder="ex: spațiu, natură, tehnologie, magie"
              disabled={loading}
              className={errors.theme ? 'error' : ''}
            />
            {errors.theme && <span className="error-message">{errors.theme}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="style">Stil Artistic</label>
            <select
              id="style"
              name="style"
              value={formData.style}
              onChange={handleInputChange}
              disabled={loading}
              className={errors.style ? 'error' : ''}
            >
              <option value="">Selectează un stil</option>
              {styleOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            {errors.style && <span className="error-message">{errors.style}</span>}
          </div>

          <div className="form-actions">
            <button
              type="submit"
              className="btn btn-primary btn-lg"
              disabled={loading || rateLimitWait > 0}
            >
              {loading ? (
                <>
                  <span className="spinner"></span>
                  Generez...
                </>
              ) : rateLimitWait > 0 ? (
                `Așteaptă ${rateLimitWait}s`
              ) : (
                'Generează Trofeu'
              )}
            </button>

            {loading && (
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleCancel}
              >
                Anulează
              </button>
            )}
          </div>

          <p className="rate-limit-info">
            Limită: 5 cereri per minut. Trofeele create sunt salvate în colecția ta.
          </p>
        </form>

        {generatedTrophy && (
          <div className="generated-trophy">
            <h2>Trofeu Generat</h2>
            <div className="trophy-preview">
              {generatedTrophy.imageDataUrl ? (
                <img
                  src={generatedTrophy.imageDataUrl}
                  alt={generatedTrophy.name}
                  className="trophy-image"
                />
              ) : (
                <div className="trophy-placeholder">🏆</div>
              )}
              <div className="trophy-info">
                <h3>{generatedTrophy.name}</h3>
                <p>{generatedTrophy.description}</p>
              </div>
            </div>
            <div className="trophy-actions">
              <button
                className="btn btn-primary"
                onClick={handleSaveTrophy}
                type="button"
              >
                Salvează în Colecție
              </button>
              <button
                className="btn btn-secondary"
                onClick={(e) => handleSubmit(e, true)}
                type="button"
              >
                Generează Alt Trofeu
              </button>
            </div>
          </div>
        )}
      </div>

      {aiTrophies.length > 0 && (
        <section className="recent-trophies">
          <h2>Trofeele Tale AI ({aiTrophies.length})</h2>
          <div className="recent-trophies-grid">
            {aiTrophies.slice(-4).reverse().map((trophy) => (
              <div key={trophy.id} className="recent-trophy-card">
                {trophy.imageDataUrl ? (
                  <img src={trophy.imageDataUrl} alt={trophy.name} />
                ) : (
                  <div className="trophy-placeholder">🏆</div>
                )}
                <span className="recent-trophy-name">{trophy.name}</span>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};
